/* De samengestelde blokken van Websitecontent (ADR 0024): het lijstje, de tekstvakken rechts en de
 * voordelen op de pagina van een oplossing, dienst of branche. In de database zijn het JSON-kolommen
 * (checklist_items, sidebar_sections, benefits).
 *
 * Het grootste risico is bestaande inhoud beschadigen. Daarom:
 *   - De bewerker houdt geen eigen kopie bij. Hij leest de JSON uit het concept en verandert alleen
 *     het onderdeel dat iemand aanraakt. De rest blijft letterlijk staan, ook sleutels die de bewerker
 *     niet kent.
 *   - Een blok dat niet is gewijzigd, wordt niet geschreven (zelfdeBlok).
 *   - Herkent leesBlok de vorm in de database niet, dan blijft het blok alleen-lezen en wordt het
 *     nooit geschreven.
 *
 * De vormen hieronder zijn gemeten op de gepubliceerde rijen van 07-10-2026 en vastgelegd in
 * tests/website/blokken-gemeten.json. Wat de site verdraagt staat in de site-repo: CheckList,
 * BenefitGrid en sidebarSections (components/groep-a/SidebarContent.tsx). */

export type BlokSoort = 'lijst' | 'tekstvakken' | 'voordelen';

export interface Blok {
  kolom: string;
  soort: BlokSoort;
  /** De naam boven het blok, in gewone taal. */
  label: string;
  /** Eén onderdeel, voor knoppen en meldingen: Punt, Tekstvak, Voordeel. */
  onderdeel: string;
  uitleg: string;
}

export const LIJST: Blok = {
  kolom: 'checklist_items', soort: 'lijst', label: 'Lijstje', onderdeel: 'Punt',
  uitleg: 'De punten met een vinkje, onder de titel van de checklist.',
};

export const TEKSTVAKKEN: Blok = {
  kolom: 'sidebar_sections', soort: 'tekstvakken', label: 'Tekstvakken rechts', onderdeel: 'Tekstvak',
  uitleg: 'De vakken met een icoon en een titel, rechts naast de tekst.',
};

export const VOORDELEN: Blok = {
  kolom: 'benefits', soort: 'voordelen', label: 'Voordelen', onderdeel: 'Voordeel',
  uitleg: 'De kaartjes met een icoon, een titel en een zin, onder de banner.',
};

/** Wat de site toont als een icoon ontbreekt of onbekend is (getIcon in de site-repo). */
export const STANDAARD_ICOON: Record<'tekstvakken' | 'voordelen', string> = { tekstvakken: 'Layout', voordelen: 'Zap' };

/* De keuzelijst. De site kent elk icoon uit lucide-react (versie 0.462, dezelfde als de CRM); dit is
 * een greep daaruit, met alle namen die op 07-10-2026 in de data staan. Een naam die hier niet staat,
 * blijft gewoon staan. tests/website/blokken.node.test.mjs kijkt na dat elke naam echt bestaat. */
export const ICONEN: string[] = [
  'Activity', 'ArrowLeftRight', 'Award', 'Banknote', 'BarChart3', 'Battery', 'BatteryCharging', 'Briefcase',
  'Building2', 'Cable', 'Calculator', 'Calendar', 'Car', 'CheckCircle', 'CircuitBoard', 'ClipboardCheck',
  'Clock', 'Coins', 'Cpu', 'Euro', 'Eye', 'Factory', 'FileBarChart', 'FileText', 'Gauge', 'Globe', 'Hammer',
  'HandCoins', 'Handshake', 'HardHat', 'Headphones', 'Layers', 'Layout', 'Leaf', 'Lightbulb', 'LineChart',
  'Lock', 'MapPin', 'Monitor', 'Network', 'PieChart', 'PiggyBank', 'Plug', 'PlugZap', 'Power', 'Recycle',
  'RefreshCw', 'Rocket', 'Scale', 'Search', 'Settings', 'ShieldCheck', 'Sprout', 'Sun', 'Target', 'Tent',
  'Timer', 'Tractor', 'TrendingUp', 'Truck', 'Users', 'UtilityPole', 'Warehouse', 'Wind', 'Wrench', 'Zap',
];

type Voorwerp = Record<string, unknown>;

const isVoorwerp = (w: unknown): w is Voorwerp => typeof w === 'object' && w !== null && !Array.isArray(w);
const isTekstOfLeeg = (w: unknown): boolean => w == null || typeof w === 'string';
const tekst = (w: unknown): string => (typeof w === 'string' ? w : '');
const lijstVan = (waarde: unknown): unknown[] => (Array.isArray(waarde) ? waarde : []);

/* ── Voordelen ─────────────────────────────────────────────────────────────── */

/* Twee schrijfwijzen in de data: t, d, i (oudere rijen) en title, description, icon. De site leest
 * beide. Een onderdeel houdt zijn eigen schrijfwijze; staan ze door elkaar, dan is de vorm onbekend. */
const KORT = { titel: 't', zin: 'd', icoon: 'i' } as const;
const LANG = { titel: 'title', zin: 'description', icoon: 'icon' } as const;
type Schrijfwijze = typeof KORT | typeof LANG;

const heeftEen = (o: Voorwerp, s: Schrijfwijze): boolean => Object.values(s).some((k) => o[k] != null);
const schrijfwijze = (o: Voorwerp): Schrijfwijze => (heeftEen(o, KORT) ? KORT : LANG);

export interface Voordeel { titel: string; zin: string; icoon: string }

export function leesVoordeel(onderdeel: unknown): Voordeel {
  const o = onderdeel as Voorwerp;
  const s = schrijfwijze(o);
  return { titel: tekst(o[s.titel]), zin: tekst(o[s.zin]), icoon: tekst(o[s.icoon]) };
}

/** Eén veld van een voordeel wijzigen. De schrijfwijze en alle andere sleutels blijven staan. */
export function zetVoordeel(onderdeel: unknown, veld: keyof Voordeel, waarde: string): Voorwerp {
  const o = onderdeel as Voorwerp;
  return { ...o, [schrijfwijze(o)[veld]]: waarde };
}

function vormVoordeel(o: unknown): string | null {
  if (!isVoorwerp(o)) return 'is geen voordeel met een titel, een zin en een icoon.';
  const kort = heeftEen(o, KORT);
  const lang = heeftEen(o, LANG);
  if (kort && lang) return 'gebruikt twee schrijfwijzen door elkaar (t, d, i en title, description, icon).';
  if (!kort && !lang) return 'heeft geen titel, zin of icoon in een vorm die de bewerker kent.';
  const mis = Object.values(kort ? KORT : LANG).find((k) => !isTekstOfLeeg(o[k]));
  return mis ? `heeft bij "${mis}" iets anders dan tekst.` : null;
}

/* ── Tekstvakken rechts ────────────────────────────────────────────────────── */

/* Drie vormen van inhoud in de data, alle drie door de site getoond:
 *   content  één tekst. Staat er een | in, dan is het eerste deel de tekst en de rest de punten.
 *   items    alleen punten, als lijst.
 *   text     alleen tekst.
 * Een vak houdt zijn eigen vorm. Een nieuw vak krijgt content: dat kan tekst én punten aan. */
export type Inhoudsvorm = 'content' | 'items' | 'text';
const INHOUD: Inhoudsvorm[] = ['items', 'content', 'text'];
export const SCHEIDING = '|';

export interface Tekstvak { icoon: string; titel: string; vorm: Inhoudsvorm; tekst: string; punten: string[] }

export function leesTekstvak(onderdeel: unknown): Tekstvak {
  const o = onderdeel as Voorwerp;
  const basis = { icoon: tekst(o.icon), titel: tekst(o.title) };
  if (o.items != null) return { ...basis, vorm: 'items', tekst: '', punten: [...(o.items as string[])] };
  if (o.content == null && o.text != null) return { ...basis, vorm: 'text', tekst: tekst(o.text), punten: [] };
  // split en join zijn elkaars omgekeerde: wat hier uit elkaar gaat, komt in zetTekstvak exact terug.
  const delen = tekst(o.content).split(SCHEIDING);
  return { ...basis, vorm: 'content', tekst: delen[0], punten: delen.slice(1) };
}

/** Een tekstvak wijzigen. De vorm van de inhoud en alle andere sleutels blijven staan. In de vorm
 *  content kan een tekst of punt geen | bevatten: dat teken scheidt de punten en valt dus weg. */
export function zetTekstvak(onderdeel: unknown, wijziging: Partial<Pick<Tekstvak, 'icoon' | 'titel' | 'tekst' | 'punten'>>): Voorwerp {
  const o: Voorwerp = { ...(onderdeel as Voorwerp) };
  if (wijziging.icoon !== undefined) o.icon = wijziging.icoon;
  if (wijziging.titel !== undefined) o.title = wijziging.titel;
  if (wijziging.tekst !== undefined || wijziging.punten !== undefined) {
    const nu = leesTekstvak(onderdeel);
    const nieuweTekst = wijziging.tekst ?? nu.tekst;
    const nieuwePunten = wijziging.punten ?? nu.punten;
    if (nu.vorm === 'items') o.items = nieuwePunten;
    else if (nu.vorm === 'text') o.text = nieuweTekst;
    else o.content = [nieuweTekst, ...nieuwePunten].map((deel) => deel.split(SCHEIDING).join('')).join(SCHEIDING);
  }
  return o;
}

function vormTekstvak(o: unknown): string | null {
  if (!isVoorwerp(o)) return 'is geen tekstvak met een icoon, een titel en inhoud.';
  for (const k of ['icon', 'title']) if (!isTekstOfLeeg(o[k])) return `heeft bij "${k}" iets anders dan tekst.`;
  const aanwezig = INHOUD.filter((k) => o[k] != null);
  if (aanwezig.length > 1) return `heeft meer dan één soort inhoud (${aanwezig.join(' en ')}).`;
  if (o.items != null && !(Array.isArray(o.items) && o.items.every((p) => typeof p === 'string'))) return 'heeft punten die geen tekst zijn.';
  for (const k of ['content', 'text']) if (!isTekstOfLeeg(o[k])) return `heeft bij "${k}" iets anders dan tekst.`;
  if (aanwezig.length === 0 && o.icon == null && o.title == null) return 'heeft geen icoon, titel of inhoud in een vorm die de bewerker kent.';
  return null;
}

/* ── Lezen, vergelijken, nakijken ──────────────────────────────────────────── */

const VORM: Record<BlokSoort, (onderdeel: unknown) => string | null> = {
  // De site roept op elk punt een tekstfunctie aan: iets anders dan tekst breekt de pagina.
  lijst: (o) => (typeof o === 'string' ? null : 'is geen tekst.'),
  tekstvakken: vormTekstvak,
  voordelen: vormVoordeel,
};

/** `reden` staat er alleen als de vorm onbekend is (ok is dan false en onderdelen is leeg). */
export interface Lezing { ok: boolean; onderdelen: unknown[]; reden?: string }

const soortNaam = (w: unknown): string => (typeof w === 'string' ? 'een losse tekst' : typeof w === 'object' ? 'een los onderdeel' : `een ${typeof w === 'number' ? 'getal' : 'losse waarde'}`);

/** Herkent de bewerker deze vorm? Leeg (null) telt als een blok zonder onderdelen. */
export function leesBlok(soort: BlokSoort, waarde: unknown): Lezing {
  if (waarde == null) return { ok: true, onderdelen: [] };
  if (!Array.isArray(waarde)) return { ok: false, onderdelen: [], reden: `Er staat geen rij onderdelen maar ${soortNaam(waarde)}.` };
  for (let i = 0; i < waarde.length; i++) {
    const reden = VORM[soort](waarde[i]);
    if (reden) return { ok: false, onderdelen: [], reden: `Onderdeel ${i + 1} ${reden}` };
  }
  return { ok: true, onderdelen: waarde };
}

/* De database (jsonb) bewaart de volgorde van sleutels niet: vergelijk dus op inhoud. */
function canoniek(w: unknown): string {
  if (Array.isArray(w)) return `[${w.map(canoniek).join(',')}]`;
  if (isVoorwerp(w)) {
    return `{${Object.keys(w).filter((k) => w[k] !== undefined).sort().map((k) => `${JSON.stringify(k)}:${canoniek(w[k])}`).join(',')}}`;
  }
  return JSON.stringify(w) ?? 'null';
}

const isLeeg = (w: unknown): boolean => w == null || (Array.isArray(w) && w.length === 0);

/** Zelfde inhoud? Dan is het blok niet aangeraakt en wordt het niet geschreven. Voor de site is
 *  een leeg blok (null) hetzelfde als een lege rij. */
export const zelfdeBlok = (a: unknown, b: unknown): boolean => (isLeeg(a) && isLeeg(b)) || canoniek(a) === canoniek(b);

/** Wat er mis is met de inhoud van een blok, of null. Eén melding tegelijk, met het nummer erbij. */
export function controleerBlok(blok: Blok, waarde: unknown): string | null {
  const lezing = leesBlok(blok.soort, waarde);
  if (!lezing.ok) return `${blok.label}: ${lezing.reden}`;
  for (let i = 0; i < lezing.onderdelen.length; i++) {
    const wat = `${blok.label}: ${blok.onderdeel.toLowerCase()} ${i + 1}`;
    const onderdeel = lezing.onderdelen[i];
    if (blok.soort === 'lijst') {
      if (!(onderdeel as string).trim()) return `${wat} is leeg. Vul het in of haal het weg.`;
    } else if (blok.soort === 'voordelen') {
      const v = leesVoordeel(onderdeel);
      if (!v.titel.trim()) return `${wat} heeft geen titel.`;
      if (!v.zin.trim()) return `${wat} heeft geen zin onder de titel.`;
    } else {
      const t = leesTekstvak(onderdeel);
      if (!t.titel.trim()) return `${wat} heeft geen titel.`;
      const leeg = t.punten.findIndex((p) => !p.trim());
      if (leeg >= 0) return `${wat} heeft een leeg punt (punt ${leeg + 1}). Vul het in of haal het weg.`;
      if (!t.tekst.trim() && t.punten.length === 0) return `${wat} heeft geen tekst en geen punten.`;
    }
  }
  return null;
}

/* ── Onderdelen toevoegen, weghalen, verplaatsen ───────────────────────────── */

/** Een leeg onderdeel om toe te voegen. Een voordeel volgt de schrijfwijze die al in het blok staat. */
export function nieuwOnderdeel(soort: BlokSoort, waarde: unknown): unknown {
  if (soort === 'lijst') return '';
  if (soort === 'tekstvakken') return { icon: STANDAARD_ICOON.tekstvakken, title: '', content: '' };
  const eerste = lijstVan(waarde).find(isVoorwerp);
  const s = eerste ? schrijfwijze(eerste) : LANG;
  return { [s.icoon]: STANDAARD_ICOON.voordelen, [s.titel]: '', [s.zin]: '' };
}

export const metOnderdeel = (waarde: unknown, i: number, onderdeel: unknown): unknown[] =>
  lijstVan(waarde).map((oud, n) => (n === i ? onderdeel : oud));

export const metNieuwOnderdeel = (waarde: unknown, onderdeel: unknown): unknown[] => [...lijstVan(waarde), onderdeel];

export const zonderOnderdeel = (waarde: unknown, i: number): unknown[] => lijstVan(waarde).filter((_, n) => n !== i);

/** Wisselt onderdeel i met zijn buur erboven (-1) of eronder (1). Aan de rand gebeurt er niets. */
export function verplaatst(waarde: unknown, i: number, stap: -1 | 1): unknown[] {
  const lijst = [...lijstVan(waarde)];
  const j = i + stap;
  if (i < 0 || i >= lijst.length || j < 0 || j >= lijst.length) return lijst;
  [lijst[i], lijst[j]] = [lijst[j], lijst[i]];
  return lijst;
}
